import { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Avatar from '@base/Avatar/Avatar';
import CommentField from '@base/CommentField/CommentField';
import Divider from '@base/Divider/Divider';
import InfiniteList from '../InfiniteList/InfiniteList';
import LikesAndReplies from './LikesAndReplies';

const Container = styled.div``;

const Wrapper = styled.div`
  display: flex;
  margin-top: 16px;
`;

const EmojiWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px 8px 12px;
  border: 1px solid
    ${({ isLiked }) =>
      isLiked ? 'var(--color-i-green)' : 'var(--color-shadow)'};
  border-radius: 8px;
  background-color: ${({ isLiked }) =>
    isLiked ? 'rgba(8, 189, 128, 0.2)' : 'auto'};
  cursor: pointer;
  margin-right: 8px;
`;

const CommentWrapper = styled(EmojiWrapper)`
  background-color: ${({ isSelected }) =>
    isSelected ? 'var(--color-shadow)' : 'auto'};
  display: flex;
  align-items: center;
`;

const User = styled.div`
  margin-top: 16px;
  display: flex;
`;

const Values = styled.span`
  color: ${({ isLiked }) =>
    isLiked ? 'var(--color-i-green)' : 'var(--color-text-primary)'};
  font-size: 12px;
  line-height: 150%;
  font-weight: 600;
  margin-left: 2px;
`;

const StyledAvatar = styled(Avatar)`
  margin-right: 8px;
`;

const UNDivider = styled(Divider)`
  margin-top: 16px;
  background-color: var(--color-shadow);
  border-radius: 8px;
`;

const LikeComment = ({
  toggleLike,
  postComment,
  getComments,
  selectedStory,
  story,
  loadMoreComments
}) => {
  const [value, setValue] = useState('');
  const username = useSelector(({ auth }) => auth.me.username.toLowerCase());
  const thumbnail = useSelector(({ auth }) => auth.me.thumbnail);
  const { is_liked, comments_count, reactions_count, uid, author } = story;

  const onChange = (e) => setValue(e.target.value);
  const addComment = () => {
    if (value === '') return;
    postComment(uid, value);
    setValue('');
  };

  const onClick = () => toggleLike(uid, is_liked);

  const updateComments = useSelector(
    (state) => state.paginator.courseUpdateComments?.[uid]
  );

  const toggleCommentField = () => getComments(story);

  const renderCommentCard = (item) => (
    <LikesAndReplies key={item.id} comment={item} storyId={uid} />
  );

  return (
    <Container>
      <Wrapper>
        {username !== author.username.toLowerCase() && (
          <EmojiWrapper isLiked={is_liked} onClick={onClick}>
            <span aria-label="like" role="img">
              👍
            </span>
            <Values isLiked={is_liked}>{reactions_count || 'Like'}</Values>
          </EmojiWrapper>
        )}
        <CommentWrapper
          isSelected={selectedStory?.uid === story.uid}
          onClick={toggleCommentField}
        >
          <span aria-label="comment" role="img">
            💬
          </span>
          <Values>{comments_count || 'Comment'}</Values>
        </CommentWrapper>
      </Wrapper>
      {selectedStory && (
        <>
          <UNDivider />
          <User>
            <StyledAvatar src={thumbnail} size="40px" />
            <CommentField
              onChange={onChange}
              value={value}
              onClick={addComment}
            />
          </User>
          <InfiniteList
            data={updateComments}
            loadMore={() => loadMoreComments(story)}
            _renderItem={renderCommentCard}
            scrollElement={document.getElementById('DrawerPaper')}
          />
        </>
      )}
    </Container>
  );
};

export default LikeComment;

LikeComment.propTypes = {
  toggleLike: PropTypes.func.isRequired,
  postComment: PropTypes.func.isRequired,
  getComments: PropTypes.func.isRequired,
  selectedStory: PropTypes.objectOf(PropTypes.any),
  story: PropTypes.objectOf(PropTypes.any).isRequired,
  loadMoreComments: PropTypes.func.isRequired
};

LikeComment.defaultProps = {
  selectedStory: null
};
